import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { SahkoisenTiliotteenAlatyyppi } from '@lemon/core-firebase/model/tiliote'
import { LocalDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidators } from '../_jaettu-angular/_validators/FormValidators'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { AsiakkaanMaksutapa } from '../_jaettu-lemonator/model/asiakas'

export interface AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogData {
  maksutapa: AsiakkaanMaksutapa
  hasActiveConnection: boolean
  latestTransactionDate?: LocalDate
  availableProviders: { value: SahkoisenTiliotteenAlatyyppi; label: string }[]
}

export interface AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogResult {
  uusiAlatyyppi: SahkoisenTiliotteenAlatyyppi
  voimassaAlkaen: LocalDate
  aktivoiHetiJosYhteysKatkeaa: boolean
}

interface PalveluntarjoajanVaihtoLomake {
  uusiAlatyyppi: SahkoisenTiliotteenAlatyyppi | null
  voimassaAlkaen: Date | null
  aktivoiHetiJosYhteysKatkeaa: boolean
}

@Component({
  templateUrl: './asiakkaan-maksutavat-aloita-provider-vaihto.dialog.html',
  styleUrls: ['./asiakkaan-maksutavat-aloita-provider-vaihto.dialog.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialog implements OnInit {
  lomake: FieldTree<PalveluntarjoajanVaihtoLomake>
  minDate: Date
  private readonly _injector = inject(Injector)

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogData,
    private _dialogRef: MatDialogRef<AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialog>,
    private _dateService: DateService,
    private _validationService: FormValidationService,
  ) {}

  ngOnInit() {
    const today = new Date()
    // Determine minimum date based on whether connection is active
    if (this.data.hasActiveConnection && this._dateService.localDateToDate(this.data.maksutapa.tilitapahtumatSynkronoidaanAlkaen) <= today) {
      // Active connection: must be future date (tomorrow or later)
      const tomorrow = this._dateService.lisaaPaivia(today, 1)
      this.minDate = tomorrow
    } else {
      // No active connection: can be retroactive, but not before latest transaction
      if (this.data.latestTransactionDate) {
        // Set to day after latest transaction
        const latestDate = this._dateService.localDateToDate(this.data.latestTransactionDate)
        this.minDate = this._dateService.lisaaPaivia(latestDate, 1)
      } else {
        // No transactions, allow any date from today onwards
        this.minDate = today
      }
    }

    this.lomake = form(
      signal<PalveluntarjoajanVaihtoLomake>({ uusiAlatyyppi: null, voimassaAlkaen: this.minDate, aktivoiHetiJosYhteysKatkeaa: false }),
      polku => {
        required(polku.uusiAlatyyppi)
        required(polku.voimassaAlkaen)
        validoiReaktiivisesti(polku.voimassaAlkaen, FormValidators.minDateValidator(this.minDate))
      },
      { injector: this._injector },
    )
  }

  getInfoMessage(): string {
    if (this.data.hasActiveConnection) {
      return `Nykyisen palveluntarjoajan (${this.data.maksutapa.sahkoisenTiliotteenAlatyyppi}) yhteys on aktiivinen, joten Lemonator hakee tietoja valittuun päivään asti (ei mukaan lukien). 
      Uusi palveluntarjoaja aloittaa tietojen haun valitusta päivästä alkaen. 
      Kun painat Tallenna, ajastetaan palveluntarjoajan vaihto ja Lemonator päivittää maksutavan automaattisesti voimaantulopäivän alussa.`
    } else {
      return `Nykyisen palveluntarjoajan (${this.data.maksutapa.sahkoisenTiliotteenAlatyyppi}) yhteys on pois päältä, joten Lemonator päivittää maksutavan heti. Uusi palveluntarjoaja hakee tietoja valitusta päivästä alkaen.`
    }
  }

  save() {
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }
    const result: AsiakkaanMaksutavatAloitaPalvelutarjoajanVaihtoDialogResult = {
      uusiAlatyyppi: this.lomake.uusiAlatyyppi().value(),
      voimassaAlkaen: this._dateService.dateToLocalDate(this.lomake.voimassaAlkaen().value()),
      aktivoiHetiJosYhteysKatkeaa: this.data.hasActiveConnection && !!this.lomake.aktivoiHetiJosYhteysKatkeaa().value(),
    }

    this._dialogRef.close(result)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  cancel() {
    this._dialogRef.close(null)
  }
}
